import { useMutation, type UseMutationOptions } from "@tanstack/react-query";

import { type CrowdcoursingApiContext, useCrowdcoursingApiContext } from "./generated/CrowdcoursingApiContext";
import { crowdcoursingApiFetch } from "./generated/CrowdcoursingApiFetcher";

export interface SalesReassignTasksPathParams {
  bubbleId: string;
}

export interface SalesReassignTasksBody {
  assignedToUserId?: string | null;
  taskIds: string[];
}

export type SalesReassignTasksVariables = {
  body: SalesReassignTasksBody;
  pathParams: SalesReassignTasksPathParams;
} & CrowdcoursingApiContext["fetcherOptions"];

/**
 * Reassigns the given tasks in one request. Kept beside the generated client until codegen
 * includes POST /api/v1/sales/bubbles/{bubbleId}/tasks/reassign.
 */
export const fetchSalesReassignTasks = (variables: SalesReassignTasksVariables, signal?: AbortSignal) =>
  crowdcoursingApiFetch<
    undefined,
    { httpStatus?: number; payload: string; status: "unknown" },
    SalesReassignTasksBody,
    object,
    object,
    SalesReassignTasksPathParams
  >({
    method: "post",
    url: "/api/v1/sales/bubbles/{bubbleId}/tasks/reassign",
    ...variables,
    signal,
  });

export const useSalesReassignTasks = (
  options?: Omit<UseMutationOptions<undefined, unknown, SalesReassignTasksVariables>, "mutationFn">,
) => {
  const { fetcherOptions } = useCrowdcoursingApiContext();
  return useMutation({
    mutationFn: (variables: SalesReassignTasksVariables) =>
      fetchSalesReassignTasks({ ...fetcherOptions, ...variables }),
    ...options,
  });
};
